<template>
   <div v-bind:class="[{ busy: loadingMedia}, { activeEnter: dragenter }]" class="media-gallery opacity-transition"
        ref="gallery">
      <pb-spinner v-if="loadingMedia"></pb-spinner>

      <!-- Filter tabs for media types -->
      <b-card v-if="!imageOnly" no-body class="mb-3">
         <b-card-header header-tag="nav">
            <b-nav card-header tabs>
               <b-nav-item
                   :active="selectedType === null"
                   @click="filterByType(null)">
                  All
               </b-nav-item>
               <b-nav-item
                   :active="selectedType === 'IMAGE'"
                   @click="filterByType('IMAGE')">
                  Images
               </b-nav-item>
               <b-nav-item
                   :active="selectedType === 'VIDEO'"
                   @click="filterByType('VIDEO')">
                  Videos
               </b-nav-item>
               <b-nav-item
                   :active="selectedType === 'DOCUMENT'"
                   @click="filterByType('DOCUMENT')">
                  Documents
               </b-nav-item>
            </b-nav>
         </b-card-header>
         <b-card-body v-if="mediaList.length > 0 || searchTerm">
            <b-row>
               <b-col>
                  <!-- Upload button and search -->
                  <b-row class="mb-3">
                     <b-col md="auto">
                        <b-button variant="primary" @click="triggerFileUpload">
                           <i class="fal fa-upload"></i> Upload Files
                        </b-button>
                        <input
                            type="file"
                            ref="fileInput"
                            @change="handleFileInputChange"
                            style="display: none"
                            multiple
                            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx">
                     </b-col>
                     <b-col>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalItems"
                            :per-page="pageSize"
                            aria-controls="mediaList"
                        ></b-pagination>
                     </b-col>
                     <b-col>
                        <b-form @submit.prevent>
                           <b-input-group>
                              <b-form-input
                                  v-model="searchTerm"
                                  v-debounce:500="handleSearch"
                                  placeholder="Search files..."
                                  type="text">
                              </b-form-input>
                              <b-input-group-append>
                                 <b-input-group-text>
                                    <i class="fal fa-search"></i>
                                 </b-input-group-text>
                              </b-input-group-append>
                           </b-input-group>
                        </b-form>
                     </b-col>
                  </b-row>

                  <!-- Media grid -->
                  <b-row v-bind:class="[{ busy: uploadingMedia}, 'opacity-transition']">
                     <pb-spinner v-if="uploadingMedia"></pb-spinner>
                     <b-col v-bind:key="media.id" md="3" class="my-3" v-for="media in mediaList">
                        <div class="media-item-wrapper">
                           <media-card
                               :media="media"
                               @click="previewMedia(media)">
                           </media-card>

                           <!-- Action buttons -->
                           <div class="media-actions" v-if="enableActions">
                              <b-button-group size="sm" class="w-100">
                                 <b-button variant="outline-info" @click.stop="generateReference(media)" v-b-tooltip.hover
                                           title="Generate ERP Reference">
                                    <i class="fal fa-file-code"></i>
                                 </b-button>
                                 <b-button variant="outline-primary" @click.stop="triggerFileReplace(media)" v-b-tooltip.hover
                                           title="Replace File">
                                    <i class="fal fa-sync-alt"></i>
                                 </b-button>
                                 <b-button variant="outline-secondary" @click.stop="editMedia(media)" v-b-tooltip.hover
                                           title="Edit">
                                    <i class="fal fa-edit"></i>
                                 </b-button>
                                 <b-button variant="outline-danger" @click.stop="confirmDelete(media)" v-b-tooltip.hover
                                           title="Delete">
                                    <i class="fal fa-trash"></i>
                                 </b-button>
                              </b-button-group>
                           </div>
                           <input
                               type="file"
                               :ref="'replaceInput_' + media.id"
                               @change="handleReplaceFile($event, media)"
                               style="display: none"
                               accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx">
                        </div>
                     </b-col>
                  </b-row>

                  <!-- Bottom pagination -->
                  <b-row>
                     <b-col>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalItems"
                            :per-page="pageSize"
                            aria-controls="mediaList"
                        ></b-pagination>
                     </b-col>
                  </b-row>
               </b-col>
            </b-row>
         </b-card-body>

         <!-- Empty state -->
         <b-card-body v-else>
            <b-row>
               <b-col class="text-center py-5">
                  <i class="fal fa-folder-open fa-3x text-muted mb-3"></i>
                  <p class="text-muted">No media files found.</p>
                  <b-button variant="primary" @click="triggerFileUpload" class="mt-2">
                     <i class="fal fa-upload"></i> Upload Files
                  </b-button>
                  <input
                      type="file"
                      ref="fileInputEmpty"
                      @change="handleFileInputChange"
                      style="display: none"
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx">
                  <p class="text-muted mt-3">Or drag and drop files here to upload.</p>
               </b-col>
            </b-row>
         </b-card-body>
      </b-card>


      <!-- Preview Modal -->
      <b-modal v-model="showPreviewModal" size="xl" :title="previewMedia ? previewMedia.originalFilename : 'Preview'" hide-footer>
         <media-preview v-if="previewingMedia" :media="previewingMedia" :show-info="true"></media-preview>

         <!-- Action buttons in preview -->
         <div class="mt-3 text-right" v-if="enableActions">
            <b-button variant="outline-secondary" @click="editMedia(previewingMedia)" class="mr-2">
               <i class="fal fa-edit"></i> Edit
            </b-button>
            <b-button variant="outline-danger" @click="confirmDelete(previewingMedia)">
               <i class="fal fa-trash"></i> Delete
            </b-button>
            <b-button variant="primary" @click="selectMediaFromPreview" class="ml-2">
               <i class="fal fa-check"></i> Select This File
            </b-button>
         </div>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" title="Edit Media" @ok="saveMediaEdit">
         <b-form v-if="editingMedia">
            <b-form-group label="Filename">
               <b-form-input v-model="editingMedia.filename"></b-form-input>
            </b-form-group>
            <b-form-group label="Description">
               <b-form-textarea v-model="editingMedia.description" rows="3"></b-form-textarea>
            </b-form-group>
            <b-form-group label="Tags (comma-separated)">
               <b-form-input v-model="editingMedia.tags"></b-form-input>
            </b-form-group>
         </b-form>
      </b-modal>
   </div>
</template>

<script>
import FileApi from "../../../shared/_api/file";
import MediaCard from './MediaCard.vue';
import MediaPreview from './MediaPreview.vue';

export default {
   name: 'MediaGallery',
   components: {
      MediaCard,
      MediaPreview
   },
   props: {
      selectImageCallback: Function,  // Legacy prop for backward compatibility
      selectMediaCallback: Function,  // New prop for media selection
      imageOnly: {                    // Set to true to only show images (legacy mode)
         type: Boolean,
         default: false
      },
      enableActions: {                // Enable edit/delete actions
         type: Boolean,
         default: true
      }
   },
   data() {
      return {
         currentPage: 1,
         pageSize: 12,
         totalItems: 0,
         mediaList: [],
         loadingMedia: false,
         searchTerm: '',
         selectedType: null,
         dragenter: false,
         uploadingMedia: false,
         showEditModal: false,
         editingMedia: null,
         showPreviewModal: false,
         previewingMedia: null
      };
   },
   mounted() {
      this.setupDragAndDrop();
   },
   created() {
      this.loadMedia();
   },
   watch: {
      currentPage() {
         this.loadMedia();
      }
   },
   methods: {
      setupDragAndDrop() {
         ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(evt => {
            this.$refs.gallery.addEventListener(evt, (e) => {
               e.preventDefault();
               e.stopPropagation();
            }, false);
         });

         this.$refs.gallery.addEventListener('dragleave', () => {
            this.dragenter = false;
         });

         this.$refs.gallery.addEventListener('dragenter', () => {
            this.dragenter = true;
         });

         this.$refs.gallery.addEventListener('drop', (e) => {
            this.dragenter = false;
            this.handleFileUpload(e.dataTransfer.files[0]);
         });
      },

      loadMedia() {
         this.loadingMedia = true;

         const params = {
            page: this.currentPage - 1,
            size: this.pageSize,
            sortBy: 'createdAt',
            sortDir: 'desc'
         };

         if (this.selectedType) {
            params.type = this.selectedType;
         }

         if (this.searchTerm) {
            params.search = this.searchTerm;
         }

         // ALWAYS use new media API, but filter to IMAGE type if imageOnly
         if (this.imageOnly) {
            params.type = 'IMAGE';
         }

         FileApi.fetchMedia(params).then(response => {
            this.mediaList = response.content || response;
            this.totalItems = response.totalElements || this.mediaList.length;
         }).finally(() => {
            this.loadingMedia = false;
         });
      },

      handleFileUpload(file) {
         this.uploadingMedia = true;

         // ALWAYS use the new media API to ensure files are tracked in database
         FileApi.uploadMedia(file).then((media) => {
            this.loadMedia(); // Reload to get updated list
            this.$bvToast.toast('File uploaded successfully', {
               title: 'Success',
               variant: 'success',
               autoHideDelay: 3000
            });
         }).catch((error) => {
            console.error('Upload error:', error);
            this.$bvToast.toast('Failed to upload file', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         }).finally(() => {
            this.uploadingMedia = false;
         });
      },

      triggerFileUpload() {
         // Handle both file input refs (one in toolbar, one in empty state)
         const fileInput = this.$refs.fileInput || this.$refs.fileInputEmpty;
         if (fileInput) {
            fileInput.click();
         }
      },

      handleFileInputChange(event) {
         const files = event.target.files;
         if (files && files.length > 0) {
            // Upload files sequentially
            this.uploadFiles(Array.from(files));
         }
         // Reset the input so the same file can be selected again
         event.target.value = '';
      },

      uploadFiles(files) {
         if (files.length === 0) return;

         this.uploadingMedia = true;
         let successCount = 0;
         let errorCount = 0;

         const uploadPromises = files.map(file =>
            FileApi.uploadMedia(file)
               .then(() => successCount++)
               .catch(() => errorCount++)
         );

         Promise.all(uploadPromises).then(() => {
            this.loadMedia();

            if (successCount > 0) {
               this.$bvToast.toast(`Successfully uploaded ${successCount} file(s)`, {
                  title: 'Success',
                  variant: 'success',
                  autoHideDelay: 3000
               });
            }

            if (errorCount > 0) {
               this.$bvToast.toast(`Failed to upload ${errorCount} file(s)`, {
                  title: 'Error',
                  variant: 'danger',
                  autoHideDelay: 5000
               });
            }
         }).finally(() => {
            this.uploadingMedia = false;
         });
      },

      filterByType(type) {
         this.selectedType = type;
         this.currentPage = 1;
         this.searchTerm = ''; // Reset search when switching tabs
         this.loadMedia();
      },

      handleSearch() {
         this.currentPage = 1;
         this.loadMedia();
      },

      previewMedia(media) {
         this.previewingMedia = media;
         this.showPreviewModal = true;
      },

      selectMedia(media) {
         // Support both legacy and new callbacks
         if (this.selectImageCallback) {
            this.selectImageCallback(media.filename);
         }
         if (this.selectMediaCallback) {
            this.selectMediaCallback(media);
         }
      },

      selectMediaFromPreview() {
         if (this.previewingMedia) {
            this.selectMedia(this.previewingMedia);
            this.showPreviewModal = false;
         }
      },

      editMedia(media) {
         this.showPreviewModal = false;
         this.editingMedia = {...media};
         this.showEditModal = true;
      },

      saveMediaEdit() {
         if (!this.editingMedia) return;

         const updateData = {
            filename: this.editingMedia.filename,
            description: this.editingMedia.description,
            tags: this.editingMedia.tags
         };

         FileApi.updateMedia(this.editingMedia.id, updateData).then(() => {
            this.$bvToast.toast('Media updated successfully', {
               title: 'Success',
               variant: 'success',
               autoHideDelay: 3000
            });
            this.loadMedia();
         }).catch(() => {
            this.$bvToast.toast('Failed to update media', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         });
      },

      triggerFileReplace(media) {
         const refKey = 'replaceInput_' + media.id;
         const inputElement = this.$refs[refKey];
         if (inputElement && inputElement[0]) {
            inputElement[0].click();
         }
      },

      handleReplaceFile(event, media) {
         const file = event.target.files[0];
         if (!file) return;

         this.$bvModal.msgBoxConfirm(
            `Are you sure you want to replace "${media.originalFilename}" with "${file.name}"? This will keep all existing references but update the physical file.`,
            {
               title: 'Confirm Replace',
               okVariant: 'warning',
               okTitle: 'Replace',
               cancelTitle: 'Cancel'
            }
         ).then(confirmed => {
            if (confirmed) {
               this.replaceMediaFile(media, file);
            }
            // Reset the input
            event.target.value = '';
         });
      },

      replaceMediaFile(media, file) {
         this.uploadingMedia = true;

         FileApi.replaceMedia(media.id, file).then(() => {
            this.$bvToast.toast('File replaced successfully', {
               title: 'Success',
               variant: 'success',
               autoHideDelay: 3000
            });
            this.loadMedia();
         }).catch((error) => {
            console.error('Replace error:', error);
            this.$bvToast.toast('Failed to replace file', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         }).finally(() => {
            this.uploadingMedia = false;
         });
      },

      confirmDelete(media) {
         this.$bvModal.msgBoxConfirm(`Are you sure you want to delete "${media.originalFilename}"?`, {
            title: 'Confirm Delete',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel'
         }).then(confirmed => {
            if (confirmed) {
               this.deleteMedia(media);
            }
         });
      },

      generateReference(media) {
         FileApi.generateReference(media.id).then((result) => {
            this.$bvToast.toast(`JSON reference file "${result.filename}" downloaded. Upload this to your ERP article files folder.`, {
               title: 'Reference Generated',
               variant: 'success',
               autoHideDelay: 5000
            });
         }).catch((error) => {
            console.error('Generate reference error:', error);
            this.$bvToast.toast('Failed to generate reference file', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         });
      },

      deleteMedia(media) {
         FileApi.deleteMedia(media.id).then(() => {
            this.$bvToast.toast('Media deleted successfully', {
               title: 'Success',
               variant: 'success',
               autoHideDelay: 3000
            });
            this.loadMedia();
         }).catch(() => {
            this.$bvToast.toast('Failed to delete media', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         });
      }
   }
}
</script>

<style lang="scss" scoped>
.media-gallery {
   min-height: 300px;
}

.activeEnter {
   border: 2px dashed #007bff;
   background-color: rgba(0, 123, 255, 0.05);
}

.media-item-wrapper {
   position: relative;

   .media-type-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.75rem;
      z-index: 10;
   }

   .media-actions {
      margin-top: 8px;
      text-align: center;
   }
}
</style>
